import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import APIError from 'types/api_error';
import { Camera } from 'types/camera';

import {
  updateQueriesData,
  UpdateQueriesDataContext,
} from 'services/update_queries_data';
import { useAPI } from 'hooks/api/useAPI';

type UseCreateReferenceDeploymentsParams = {
  camera_id: string;
  deployment_ids: string[];
};

/** Add reference deployments to a universal bridge camera. */
export function useCreateReferenceDeployments({
  onMutate,
  onError,
  onSettled,
  ...options
}: UseMutationOptions<
  string[],
  APIError,
  UseCreateReferenceDeploymentsParams,
  UpdateQueriesDataContext<Camera>
> = {}) {
  const queryClient = useQueryClient();
  const { applicationID, client } = useAPI();

  return useMutation(
    async ({ camera_id, deployment_ids }) => {
      const { data } = await client.post(
        `/apps/${applicationID}/cameras/${camera_id}/reference_deployments`,
        deployment_ids
      );

      return data;
    },
    {
      ...options,
      async onMutate(params) {
        await queryClient.cancelQueries(['cameras']);
        await queryClient.cancelQueries(['camera']);

        onMutate?.(params);

        const { camera_id, deployment_ids } = params;

        return updateQueriesData({
          queryClient,
          listQueryKey: ['cameras'],
          singleQueryKey: ['camera'],
          ids: [camera_id],
          updateData(camera) {
            const updatedCamera = structuredClone(camera);

            updatedCamera.reference_deployment_ids = [
              ...new Set([
                ...updatedCamera.reference_deployment_ids,
                ...deployment_ids,
              ]),
            ];

            return updatedCamera;
          },
        });
      },
      async onError(error, params, context) {
        onError?.(error, params, context);

        context?.previousLists?.forEach(([queryKey, data]) =>
          queryClient.setQueryData(queryKey, data)
        );
        context?.previousSingles?.forEach(([queryKey, data]) =>
          queryClient.setQueryData(queryKey, data)
        );
      },
      onSettled(...args) {
        onSettled?.(...args);

        queryClient.invalidateQueries(['cameras']);
        queryClient.invalidateQueries(['camera']);
      },
    }
  );
}
